/**
 * Strips TypeScript type annotations to produce executable JavaScript
 * More robust implementation for playground use
 */
export function stripTypes(tsCode: string): string {
  let result = tsCode;

  // Remove single-line interface declarations
  // interface Foo { prop: type; }
  result = result.replace(/^\s*interface\s+\w+[^{]*\{[^}]*\}\s*$/gm, '');

  // Remove multi-line interface declarations
  // We need to handle nested braces carefully
  result = removeBlockDeclarations(result, 'interface');

  // Remove type alias declarations
  // type Foo = ...;
  result = result.replace(/^\s*type\s+\w+\s*=\s*[^;]+;\s*$/gm, '');

  // Remove enum declarations
  result = removeBlockDeclarations(result, 'enum');

  // Remove access modifiers (private, public, protected, readonly)
  result = result.replace(/\b(private|public|protected|readonly)\s+/g, '');

  // Remove class property declarations (lines with just "name: Type;")
  // These are TypeScript property declarations without initializers
  // e.g., "  nom: string;" or "  age: number;"
  result = result.replace(/^\s*\w+\s*:\s*[A-Za-z_]\w*(?:<[^>]+>)?(?:\[\])?\s*;\s*$/gm, '');

  // Remove type from class property declarations WITH initializers
  // e.g., "name: string = 'foo';" -> "name = 'foo';"
  result = result.replace(/^(\s*\w+)\s*:\s*[A-Za-z_]\w*(?:<[^>]+>)?(?:\[\])?\s*(=)/gm, '$1 $2');

  // Remove 'implements' clause from classes
  // class Foo implements Bar, Baz { -> class Foo {
  result = result.replace(/\s+implements\s+[\w,\s]+(?=\s*\{)/g, '');

  // Remove 'extends' with type parameters from interfaces (already removed)
  // but handle class extends which should stay

  // Remove generic type parameters from function/class declarations
  // function foo<T>() -> function foo()
  // class Foo<T> -> class Foo
  result = result.replace(/(<\s*\w+(?:\s*,\s*\w+)*\s*>)(?=\s*[\(\{])/g, '');

  // Remove function/method return type annotations
  // function foo(): number { -> function foo() {
  // async function foo(): Promise<void> { -> async function foo() {
  // (): void => { -> () => {
  // saluer(): void { -> saluer() {
  // Handle generic types like Promise<void> by matching balanced angle brackets
  result = result.replace(/\)\s*:\s*[A-Za-z_]\w*(?:<[^>]*>)?(?:\[\])?\s*(?=\s*\{)/g, ')');
  // Handle arrow function return types: () => { or (): Type => expr
  result = result.replace(/\)\s*:\s*[A-Za-z_]\w*(?:<[^>]*>)?(?:\[\])?\s*(?=\s*=>)/g, ')');

  // Remove type annotations from variable declarations
  // const x: number = 1 -> const x = 1
  // let arr: string[] = [] -> let arr = []
  // const p: Promise<void> = ... -> const p = ...
  result = result.replace(/((?:const|let|var)\s+\w+)\s*:\s*[^=;\n]+(?=\s*[=;])/g, '$1');

  // Remove type annotations from function parameters
  // We need to be careful with nested generics and arrow functions
  // (x: string, y: number) -> (x, y)
  // (arr: string[]) -> (arr)
  // (p: Promise<string>) -> (p)
  result = removeParameterTypes(result);

  // Remove type assertions
  // x as string -> x
  // (x as Type) -> (x)
  result = result.replace(/\s+as\s+\w+(?:<[^>]+>)?/g, '');

  // Clean up extra whitespace and empty lines
  result = result.replace(/\n\s*\n\s*\n/g, '\n\n');
  result = result.replace(/^\s*[\r\n]/gm, '');

  return result.trim();
}

/**
 * Remove block declarations like interface and enum
 * Handles nested braces
 */
function removeBlockDeclarations(code: string, keyword: string): string {
  const regex = new RegExp(`^\\s*${keyword}\\s+\\w+[^{]*\\{`, 'gm');
  let result = code;
  let match;

  while ((match = regex.exec(result)) !== null) {
    const startIndex = match.index;
    let braceCount = 1;
    let endIndex = match.index + match[0].length;

    // Find matching closing brace
    while (braceCount > 0 && endIndex < result.length) {
      if (result[endIndex] === '{') braceCount++;
      if (result[endIndex] === '}') braceCount--;
      endIndex++;
    }

    // Remove the entire declaration
    result = result.slice(0, startIndex) + result.slice(endIndex);
    regex.lastIndex = startIndex; // Reset regex position
  }

  return result;
}

/**
 * Remove type annotations from function parameters
 * Handles generics like Promise<string>
 */
function removeParameterTypes(code: string): string {
  let result = code;

  // Match parameters with type annotations
  // This handles: name: Type, name: Type<Generic>, name: Type[]
  const paramRegex = /(\w+)\s*:\s*([A-Za-z_]\w*(?:<[^>]+>)?(?:\[\])?)/g;

  // We need to only apply this within parentheses (function parameters)
  // Process each parenthesized group
  result = result.replace(/\(([^)]*)\)/g, (match, inner) => {
    // Remove type annotations from parameters inside parentheses
    const cleaned = inner.replace(paramRegex, '$1');
    return `(${cleaned})`;
  });

  return result;
}
